import { useMemo, useEffect } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { DashboardPage } from '~/components/ui/dashboard/DashboardPage';
import { canAccess } from '~/utils/rbac';
import { useEnsureDashboard } from '~/hooks/useEnsureDashboard';
import { ErrorComponent } from '~/routes/__root';
import { portfolioIdAtom } from '~/atoms/dashboardAtom';
import type { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

// Define which widgets are allowed on the risks page
const ALLOWED_RISK_WIDGETS = [
  'risk-alerts',
  'projected-risk-metrics',
  'expected-impact',
  'market-analysis',
  'news-impact-analysis',
] as const;

/**
 * Default layout positions for risk dashboard
 */
const RISK_LAYOUT = [
  { h: 6, w: 4, x: 4, y: 0 },
  { h: 5, w: 4, x: 8, y: 0 },
  { h: 16, w: 4, x: 0, y: 0 },
  { h: 10, w: 4, x: 4, y: 6 },
  { h: 11, w: 4, x: 8, y: 5 },
];

const RISK_WIDGET_IDS = [
  'projected-risk-metrics',
  'expected-impact',
  'risk-alerts',
  'market-analysis',
  'news-impact-analysis',
];

/**
 * Creates widget configs for risk dashboard with default layout
 * @param withUUID - If true, generates unique IDs with UUIDs; if false, uses simple widget IDs for reset matching
 */
const createRiskDashboardConfig = (withUUID = true): Config[] => {
  return RISK_LAYOUT.map((position, index) => ({
    ...position,
    id: withUUID
      ? `${RISK_WIDGET_IDS[index]}-widget-${crypto.randomUUID()}`
      : RISK_WIDGET_IDS[index],
  }));
};

function RisksComponent() {
  const { portfolioId } = Route.useParams();
  const setPortfolioId = useSetAtom(portfolioIdAtom);

  // Update portfolioId atom when route changes
  useEffect(() => {
    if (portfolioId) {
      setPortfolioId(portfolioId);
    }
  }, [portfolioId, setPortfolioId]);

  const initialRiskConfig = useMemo(() => createRiskDashboardConfig(true), []);
  const riskDefaultLayout = useMemo(() => createRiskDashboardConfig(false), []);

  const { dashboardId, error } = useEnsureDashboard({
    type: 'risk',
    name: 'Risk Dashboard',
    initialConfig: initialRiskConfig,
    portfolioId,
  });

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <DashboardPage
      dashboardId={dashboardId || ''}
      breadcrumbLabel="Risks and Opportunities"
      breadcrumbUrl={`/portfolio/${portfolioId}/risks`}
      pageTaglinePrefix="Risks and Opportunities"
      allowedWidgets={ALLOWED_RISK_WIDGETS}
      initialLayout={riskDefaultLayout}
      emptyStateTitle="No Risk Widgets Found"
      emptyStateMessage="This dashboard doesn't have any risk widgets yet. Add some risk widgets to get started."
      emptyStateButtonLabel="Add Risk Widget"
    />
  );
}

const RisksRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);
  if (!allowed) return null;
  return <RisksComponent />;
};

export const Route = createFileRoute(
  '/_authLayout/portfolio/$portfolioId/risks',
)({
  beforeLoad: async () => {
    if (!canAccess('dashboard')) {
      throw notFound();
    }
  },
  component: RisksRoute,
});
